import React, { useEffect, useState } from "react";
import "../styles/client.css";
import { createClient, createContactPerson } from "../api/clientService";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

interface Contact {
    name: string;
    phone: string;
    email: string;
    designation: string;
}

const ClientForm: React.FC = () => {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const [formData, setFormData] = useState({
        clientName: "",
        pan: "",
        gstin: "",
        billingAddress: {
            address1: "",
            address2: "",
            state: "",
            city: "",
            pincode: "",
        },
        contacts: [
            { name: "", phone: "", email: "", designation: "" },
        ] as Contact[],
    });
    // const [clientSegmentOptions, setClientSegmentOptions] = useState<
    //     { value: number; label: string }[]
    // >([]);

    // useEffect(() => {
    //     getClientSegmentMaster()
    //         .then(res => {
    //             setClientSegmentOptions(
    //                 res.data.results.map((s: any) => ({
    //                     value: s.id,
    //                     label: `${s.name} (${s.vertical_name})`,
    //                 }))
    //             );
    //         })
    //         .catch(() => {
    //             toast.error("Failed to load client segments");
    //         });
    // }, []);


    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        /* ===== Client Details ===== */
        if (!formData.clientName.trim()) {
            newErrors.clientName = "Client name is required";
        }

        // if (!formData.clientSegment) {
        //     newErrors.clientSegment = "Client segment is required";
        // }

        /* PAN format (optional but validated if entered) */
        if (
            formData.pan &&
            !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan)
        ) {
            newErrors.pan = "Invalid PAN format";
        }

        /* GST format (optional but validated if entered) */
        if (
            formData.gstin &&
            !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gstin)
        ) {
            newErrors.gstin = "Invalid GST number";
        }
        /* ===== Pincode (optional but must be valid if entered) ===== */
        if (
            formData.billingAddress.pincode &&
            !/^[1-9][0-9]{5}$/.test(formData.billingAddress.pincode)
        ) {
            newErrors.pincode = "Enter a valid 6-digit pincode";
        }


        /* ===== Contact Details ===== */
        formData.contacts.forEach((contact, index) => {
            if (!contact.name.trim()) {
                newErrors[`contact_name_${index}`] =
                    "Contact name is required";
            }

            if (!contact.phone.trim()) {
                newErrors[`contact_phone_${index}`] =
                    "Contact number is required";
            } else if (!/^[6-9]\d{9}$/.test(contact.phone)) {
                newErrors[`contact_phone_${index}`] =
                    "Invalid mobile number";
            }

            if (
                contact.email &&
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)
            ) {
                newErrors[`contact_email_${index}`] =
                    "Invalid email format";
            }

            if (!contact.designation.trim()) {
                newErrors[`contact_designation_${index}`] =
                    "Designation is required";
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /* ---------------- handlers ---------------- */

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddressChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            billingAddress: {
                ...prev.billingAddress,
                [name]: value,
            },
        }));
    };

    const handleContactChange = (
        index: number,
        field: keyof Contact,
        value: string
    ) => {
        const updated = [...formData.contacts];
        updated[index][field] = value;
        setFormData(prev => ({ ...prev, contacts: updated }));
    };

    const addContact = () => {
        setFormData(prev => ({
            ...prev,
            contacts: [
                ...prev.contacts,
                { name: "", phone: "", email: "", designation: "" },
            ],
        }));
    };

    const removeContact = (index: number) => {
        if (index === 0) return;
        setFormData(prev => ({
            ...prev,
            contacts: prev.contacts.filter((_, i) => i !== index),
        }));
    };

    /* ---------------- SUBMIT ---------------- */

    const handleSubmit = async () => {
        if (!validateForm()) {
            toast.error("Please fix the highlighted errors");
            return;
        }
        try {
            setLoading(true);

            /* ========== 1️⃣ CREATE CLIENT ========== */
            const clientRes = await createClient({
                name: formData.clientName,
                pan_no: formData.pan,
                gst_no: formData.gstin,
                address_line_1: formData.billingAddress.address1,
                address_line_2: formData.billingAddress.address2,
                city: formData.billingAddress.city,
                state: formData.billingAddress.state,
                pincode: formData.billingAddress.pincode,
            });

            const customerId = clientRes?.data?.data?.id;

            if (!customerId) {
                throw new Error("Customer ID not received");
            }

            /* ========== 2️⃣ CREATE CONTACT PERSONS ========== */
            for (let i = 0; i < formData.contacts.length; i++) {
                const contact = formData.contacts[i];

                await createContactPerson({
                    customer_id: customerId,
                    name: contact.name,
                    mobile_no: contact.phone,
                    email: contact.email,
                    designation: contact.designation,
                    is_master: i === 0,
                });
            }

            toast.success("Client created successfully");
            navigate("/masters/client");

        } catch (error: any) {
            console.error(error);
            toast.error(
                error?.response?.data?.message || "Failed to create client"
            );
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- render ---------------- */

    return (
        <div className="card common-form-card">

            {/* ================= CLIENT DETAILS ================= */}
            <div className="form-section">
                <div className="form-section-header bg-primary text-white">
                    Client Details
                </div>

                <div className="row g-3">
                    <div className="col-md-3">
                        <div className="form-floating">
                            <input
                                className={`form-control ${errors.clientName ? "is-invalid" : ""}`}
                                name="clientName"
                                placeholder="Client Name"
                                value={formData.clientName}
                                onChange={handleChange}
                            />
                            <label>Client Name *</label>
                            {errors.clientName && (
                                <div className="invalid-feedback">{errors.clientName}</div>
                            )}
                        </div>

                    </div>

                    {/* <div className="col-md-3">
                        <div className="form-floating">
                            <select
                                className={`form-select ${errors.clientSegment ? "is-invalid" : ""}`}
                                name="clientSegment"
                                value={formData.clientSegment}
                                onChange={handleChange}
                            >
                                <option value="">Select</option>

                                {clientSegmentOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>

                            <label>Client Segment *</label>
                            {errors.clientSegment && (
                                <div className="invalid-feedback">{errors.clientSegment}</div>
                            )}
                        </div>
                    </div> */}

                    <div className="col-md-3">
                        <div className="form-floating">
                            <input
                                className={`form-control ${errors.pan ? "is-invalid" : ""}`}
                                name="pan"
                                placeholder="PAN"
                                value={formData.pan}
                                onChange={handleChange}
                            />
                            <label>PAN</label>
                            {errors.pan && <div className="invalid-feedback">{errors.pan}</div>}

                        </div>
                    </div>

                    <div className="col-md-3">
                        <div className="form-floating">
                            <input
                                className={`form-control ${errors.gstin ? "is-invalid" : ""}`}
                                name="gstin"
                                placeholder="GST IN"
                                value={formData.gstin}
                                onChange={handleChange}
                            />
                            <label>GST IN</label>
                            {errors.gstin && <div className="invalid-feedback">{errors.gstin}</div>}

                        </div>
                    </div>
                </div>
            </div>

            {/* ================= CONTACT DETAILS ================= */}
            <div className="form-section mt-4">
                <div className="form-section-header bg-primary text-white d-flex justify-content-between align-items-center">
                    <span>Contact Details</span>
                    <button
                        type="button"
                        className="btn btn-sm btn-light"
                        onClick={addContact}
                    >
                        Add Contact
                    </button>
                </div>

                {formData.contacts.map((contact, index) => (
                    <div className="row g-3 align-items-center mb-2" key={index}>

                        {/* Contact Name */}
                        <div className="col-md-3">
                            <div className="form-floating">
                                <input
                                    className={`form-control ${errors[`contact_name_${index}`] ? "is-invalid" : ""
                                        }`}
                                    placeholder="Contact Name"
                                    value={contact.name}
                                    onChange={e =>
                                        handleContactChange(index, "name", e.target.value)
                                    }
                                />
                                <label>Contact Name *</label>
                                {errors[`contact_name_${index}`] && (
                                    <div className="invalid-feedback">
                                        {errors[`contact_name_${index}`]}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Contact Number */}
                        <div className="col-md-3">
                            <div className="form-floating">
                                <input
                                    className={`form-control ${errors[`contact_phone_${index}`] ? "is-invalid" : ""
                                        }`}
                                    placeholder="Contact Number"
                                    value={contact.phone}
                                    onChange={e =>
                                        handleContactChange(index, "phone", e.target.value)
                                    }
                                />
                                <label>Contact Number *</label>
                                {errors[`contact_phone_${index}`] && (
                                    <div className="invalid-feedback">
                                        {errors[`contact_phone_${index}`]}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Email */}
                        <div className="col-md-3">
                            <div className="form-floating">
                                <input
                                    className={`form-control ${errors[`contact_email_${index}`] ? "is-invalid" : ""
                                        }`}
                                    placeholder="Email"
                                    value={contact.email}
                                    onChange={e =>
                                        handleContactChange(index, "email", e.target.value)
                                    }
                                />
                                <label>Email ID</label>
                                {errors[`contact_email_${index}`] && (
                                    <div className="invalid-feedback">
                                        {errors[`contact_email_${index}`]}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Designation */}
                        <div className="col-md-2">
                            <div className="form-floating">
                                <input
                                    className={`form-control ${errors[`contact_designation_${index}`] ? "is-invalid" : ""
                                        }`}
                                    placeholder="Designation"
                                    value={contact.designation}
                                    onChange={e =>
                                        handleContactChange(index, "designation", e.target.value)
                                    }
                                />
                                <label>Designation *</label>
                                {errors[`contact_designation_${index}`] && (
                                    <div className="invalid-feedback">
                                        {errors[`contact_designation_${index}`]}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Remove */}
                        <div className="col-md-1 text-end">
                            {index !== 0 && (
                                <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => removeContact(index)}
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    </div>
                ))}

            </div>

            {/* ================= BILLING ADDRESS ================= */}
            <div className="form-section mt-4 border-0">
                <div className="form-section-header bg-primary text-white">
                    Billing Address
                </div>

                <div className="row g-3">
                    {[
                        ["address1", "Address Line 1"],
                        ["address2", "Address Line 2"],
                        ["state", "State"],
                        ["city", "City"],
                        ["pincode", "Pincode"],
                    ].map(([key, label]) => (
                        <div className="col-md-3" key={key}>
                            <div className="form-floating">
                                <input
                                    className={`form-control ${key === "pincode" && errors.pincode ? "is-invalid" : ""
                                        }`}
                                    name={key}
                                    placeholder={label}
                                    value={(formData.billingAddress as any)[key]}
                                    onChange={handleAddressChange}
                                />
                                <label>{label}</label>

                                {key === "pincode" && errors.pincode && (
                                    <div className="invalid-feedback">
                                        {errors.pincode}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                </div>
            </div>

            {/* ================= FOOTER ================= */}
            <div className="form-footer d-flex justify-content-end gap-2 mt-4">

                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate("/masters/client")}
                >
                    Cancel
                </button>
                <button
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading ? "Saving..." : "Create"}
                </button>
            </div>
        </div>
    );
};

export default ClientForm;
